// Tryb — sign in.
//
// One door for login and signup: a phone number, a six-digit code, and you're
// either back in your studio or you have a new one. There is no password to
// forget and nothing to remember.

import { useEffect, useRef, useState } from "react";
import { Button, Input } from "../ds/index.js";
import logo from "../assets/tryb-logo.svg";
import { api } from "../lib/api.js";
import {
  initMsg91,
  msg91Config,
  msg91Enabled,
  retryOtp as msg91RetryOtp,
  sendOtp as msg91SendOtp,
  verifyOtp as msg91VerifyOtp,
} from "../lib/msg91.js";

// MSG91 sends and checks the code when BOTH sides are set up; otherwise our own
// API does. The screen is identical either way — only these two calls differ.
//
// The server decides, not the browser: it is the only side that knows whether
// it holds the authkey needed to verify a widget token. Deciding here from the
// build-time vars alone would put the UI into widget mode against a server that
// could not verify anything, and sign-in would fail for everyone.
const SELF = { mode: "self", codeLength: 6 };
const WIDGET = { mode: "msg91-widget", codeLength: msg91Config.otpLength };

/** E.164 without the leading plus, which is what MSG91 expects. */
const msg91Identifier = (phone) => {
  const digits = String(phone).replace(/\D/g, "");
  return digits.length === 10 ? `91${digits}` : digits;
};

const mono = {
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  letterSpacing: "var(--tracking-wider)",
  textTransform: "uppercase",
};

export default function Login({ onSignedIn }) {
  // Until the server answers, assume our own flow — it always works.
  const [auth, setAuth] = useState(SELF);
  const usingMsg91 = auth.mode === "msg91-widget";
  const CODE_LENGTH = auth.codeLength;

  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState(null);
  const [devCode, setDevCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(0);

  const codeRef = useRef(null);

  useEffect(() => {
    api
      .authConfig()
      .then((c) => {
        const widget = c.mode === "msg91-widget" && msg91Enabled();
        setAuth(widget ? WIDGET : SELF);
        // Start MSG91 now rather than on the first click: it needs a moment to
        // fetch a captcha token, and doing both at once fails the first send.
        if (widget) initMsg91().catch(() => {});
      })
      .catch(() => setAuth(SELF));
  }, []);

  useEffect(() => {
    if (step === "code") codeRef.current?.focus();
  }, [step]);

  // Resend is rate limited server-side; the countdown just says so up front.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async (e) => {
    e?.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);

    try {
      if (usingMsg91) {
        if (step === "code") await msg91RetryOtp();
        else await msg91SendOtp(msg91Identifier(phone));
        setSentTo(maskLocally(phone));
        setDevCode(null);
      } else {
        const res = await api.requestOtp(phone);
        setSentTo(res.maskedPhone);
        setDevCode(res.devCode || null);
      }
      setCode("");
      setStep("code");
      setCooldown(30);
    } catch (err) {
      setError(err.hint ? `${err.message} — ${err.hint}` : err.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (busy || code.length < CODE_LENGTH) return;
    setBusy(true);
    setError(null);

    try {
      const res = usingMsg91
        // MSG91 checks the code and hands back an access token; the API turns
        // that into a session, and only the API can vouch for whose it is.
        ? await api.verifyWidgetToken(await msg91VerifyOtp(code), phone)
        : await api.verifyOtp(phone, code);
      onSignedIn(res.user, res.isNewAccount);
    } catch (err) {
      setError(err.hint ? `${err.message} — ${err.hint}` : err.message);
      setCode("");
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", background: "var(--surface-page)" }}>
      <aside
        style={{
          background: "var(--ink-900)",
          color: "var(--sand-75)",
          padding: "48px 56px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <img src={logo} alt="tryb" style={{ width: 84, height: 84 }} />

        <div style={{ position: "relative", maxWidth: 460 }}>
          <div style={{ ...mono, opacity: 0.6, marginBottom: 14 }}>Facilitator studio</div>
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-3xl)",
              fontWeight: 800,
              letterSpacing: "var(--tracking-tight)",
              lineHeight: 1.05,
            }}
          >
            Hold the room where strangers become a tryb.
          </h1>
          <p style={{ margin: "14px 0 0", fontSize: "var(--text-md)", opacity: 0.82, lineHeight: 1.55 }}>
            We curate who’s in the room, handle the bookings and the reminders, and measure the connection that comes out of it.
          </p>
        </div>

        <div style={{ fontSize: "var(--text-xs)", opacity: 0.6, lineHeight: 1.5, maxWidth: 420 }}>
          Your number is how we reach you, and how members find you at the door three hours before a session. Nothing else.
        </div>
      </aside>

      <main style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 40px" }}>
        <div style={{ width: "100%", maxWidth: 380 }}>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-2xl)",
              fontWeight: 800,
              letterSpacing: "var(--tracking-tight)",
            }}
          >
            {step === "phone" ? "Log in or sign up" : "Check your phone"}
          </h2>
          <p style={{ margin: "6px 0 26px", fontSize: "var(--text-sm)", color: "var(--text-secondary)", lineHeight: 1.55 }}>
            {step === "phone" ? (
              "to host a tryb"
            ) : (
              <>
                We sent a {CODE_LENGTH}-digit code to <strong style={{ color: "var(--text-primary)" }}>{sentTo}</strong>.
              </>
            )}
          </p>

          {step === "phone" ? (
            <form onSubmit={requestCode} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                autoFocus
                placeholder="98450 12345"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                iconLeft={<span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-sm)" }}>+91</span>}
                error={error ? " " : ""}
              />
              <Button type="submit" variant="primary" size="lg" block disabled={busy || phone.trim().length < 6}>
                {busy ? "Sending…" : "Send code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Input
                ref={codeRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="······"
                maxLength={CODE_LENGTH}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
                error={error ? " " : ""}
                style={{ fontFamily: "var(--font-mono)" }}
              />
              <Button type="submit" variant="primary" size="lg" block disabled={busy || code.length < CODE_LENGTH}>
                {busy ? "Checking…" : "Continue"}
              </Button>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <button
                  type="button"
                  onClick={() => { setStep("phone"); setError(null); setDevCode(null); }}
                  style={linkButton}
                >
                  Change number
                </button>
                <button
                  type="button"
                  onClick={requestCode}
                  disabled={cooldown > 0 || busy}
                  style={{ ...linkButton, opacity: cooldown > 0 ? 0.5 : 1, cursor: cooldown > 0 ? "default" : "pointer" }}
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                </button>
              </div>
            </form>
          )}

          {error && (
            <div style={{ marginTop: 14, fontSize: "var(--text-sm)", color: "var(--crimson-600)", fontWeight: 600, lineHeight: 1.5 }}>
              {error}
            </div>
          )}

          {devCode && (
            <div
              style={{
                marginTop: 18,
                padding: "12px 14px",
                borderRadius: "var(--radius-md)",
                background: "var(--honey-100)",
                border: "1px solid var(--honey-300)",
                fontSize: "var(--text-xs)",
                color: "var(--text-secondary)",
                lineHeight: 1.5,
              }}
            >
              <strong style={{ color: "var(--text-primary)" }}>No SMS provider configured.</strong> Your code is{" "}
              <code style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-sm)", color: "var(--text-primary)" }}>{devCode}</code>.
              Add Twilio or MSG91 credentials to <code style={{ fontFamily: "var(--font-mono)" }}>.env</code> to send real texts.
            </div>
          )}

          <p style={{ margin: "26px 0 0", fontSize: "var(--text-xs)", color: "var(--text-muted)", lineHeight: 1.55 }}>
            By continuing you agree to host by the tryb code. We’ll never share your number with members until three hours before a session.
          </p>
        </div>
      </main>
    </div>
  );
}

/** Masked for display only — the API does the real masking when it answers. */
function maskLocally(phone) {
  const digits = String(phone).replace(/\D/g, "");
  const e164 = digits.length === 10 ? `+91${digits}` : `+${digits}`;
  return `${e164.slice(0, e164.length - 6)}····${e164.slice(-2)}`;
}

const linkButton = {
  border: "none",
  background: "transparent",
  padding: 0,
  cursor: "pointer",
  fontFamily: "var(--font-body)",
  fontSize: "var(--text-sm)",
  fontWeight: 600,
  color: "var(--text-brand)",
};
